/**
 * Copyright © 2026 Adnuntius AS.
 */
import {AfterViewInit, Component, ElementRef, OnDestroy} from '@angular/core';
import {BsModalRef, BsModalService} from 'ngx-bootstrap/modal';

@Component({
  standalone: true,
  selector: 'adn-confirm-navigation-modal',
  template: `
    <div class="modal-header">
      <h4 class="modal-title">You have unsaved changes</h4>
    </div>
    <div class="modal-body">
      <p>You have unsaved changes that will be lost if you leave the page.</p>
      <p>Do you still want to leave the page and lose your unsaved changes?</p>
      <p class="text-center">
        <button type="button" class="btn btn-danger" data-adn-confirm="leave">Leave Page, Ignore Unsaved Changes</button>
        <button type="button" class="btn btn-info" data-adn-confirm="stay">Stay on Page</button>
      </p>
    </div>
  `,
  styles: [`
    .btn + .btn {
      margin-left: 8px;
    }
  `]
})
export class ConfirmNavigationModalComponent implements AfterViewInit, OnDestroy {
  /** Set via BsModalService initialState / ConfirmNavigationModalService */
  onLeave: () => void;
  /** Set via BsModalService initialState / ConfirmNavigationModalService */
  onStay: () => void;

  private removeListeners: Array<() => void> = [];

  constructor(
    public readonly bsModalRef: BsModalRef,
    private readonly bsModalService: BsModalService,
    private readonly host: ElementRef<HTMLElement>
  ) {
  }

  ngAfterViewInit(): void {
    // Native listeners — Puppeteer / zone edge cases sometimes skip Angular (click)
    // on ngx-bootstrap modal content. DOM click always reaches these.
    const leaveBtn = this.host.nativeElement.querySelector('[data-adn-confirm="leave"]');
    const stayBtn = this.host.nativeElement.querySelector('[data-adn-confirm="stay"]');
    if (leaveBtn) {
      const onLeaveClick = (event: Event) => {
        event.preventDefault();
        event.stopPropagation();
        this.leave();
      };
      leaveBtn.addEventListener('click', onLeaveClick);
      this.removeListeners.push(() => leaveBtn.removeEventListener('click', onLeaveClick));
    }
    if (stayBtn) {
      const onStayClick = (event: Event) => {
        event.preventDefault();
        event.stopPropagation();
        this.stay();
      };
      stayBtn.addEventListener('click', onStayClick);
      this.removeListeners.push(() => stayBtn.removeEventListener('click', onStayClick));
    }
  }

  ngOnDestroy(): void {
    this.removeListeners.forEach((fn) => fn());
    this.removeListeners = [];
  }

  leave(): void {
    this.onLeave?.();
    this.forceHide();
  }

  stay(): void {
    this.onStay?.();
    this.forceHide();
  }

  /**
   * bsModalRef.hide() delegates to ModalContainerComponent.hide(), which no-ops
   * while isShown is still false (set in a setTimeout(0) after open — even with
   * animated:false). BsModalService.hide(id) always tears down the loader/backdrop.
   */
  private forceHide(): void {
    const id = this.bsModalRef.id;
    if (id !== null && id !== undefined) {
      this.bsModalService.hide(id);
      return;
    }
    this.bsModalRef.hide();
  }
}
